import * as THREE from 'three';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  defaultVisibilityBox,
  defaultVisibilityBoxStyle,
  type RotatedVisibilityBoxProps,
  defaultImageAnchorsExtraRange,
  VisibilityBoxMode,
} from './VisibilityBox';
import type { Number3 } from '../types';
import { PointCloudMaterial, type VisibilityBoxUniforms } from './PointCloudMaterial';
import { useAbyssViewerContext } from './AbyssViewerContext';
import { isPointInsideCuboidMatrix, matrixFromCuboid } from './Util';

export type VisibilityBoxExtended = RotatedVisibilityBoxProps & {
  matrix: THREE.Matrix4;
  matrixInverse: THREE.Matrix4;
  checkIsInside: (point: Number3) => boolean;
};

export const getBoundingBoxOfRotatedVBox = (vboxMatrix: THREE.Matrix4): THREE.Box3 => {
  const point = new THREE.Vector3();
  const box = new THREE.Box3();
  // generate 8 vertices of vbox
  [...Array(8).keys()].map((bits: number) => {
    const xyz: Number3 = [0, 1, 2].map((coord: number) => {
      return bits & (1 << coord) ? 0.5 : -0.5;
    }) as Number3;
    point.set(...xyz);
    point.applyMatrix4(vboxMatrix);
    box.expandByPoint(point);
  });
  return box;
};

const matrixFromVisibilityBox = (visibilityBox: RotatedVisibilityBoxProps) => {
  let matrix: THREE.Matrix4 | undefined;
  if (visibilityBox.mode === VisibilityBoxMode.Disabled) {
    matrix = new THREE.Matrix4();
    const quat0 = new THREE.Quaternion();
    const position0 = new THREE.Vector3(0, 0, 0);
    const scale0 = new THREE.Vector3(100_000_000, 100_000_000, 100_000_000);
    matrix.compose(position0, quat0, scale0);
  } else {
    matrix = matrixFromCuboid(visibilityBox);
  }
  return matrix;
};

const useVisibilityBoxExtended = (
  visibilityBox: RotatedVisibilityBoxProps,
  matrix: THREE.Matrix4,
  matrixInverse: THREE.Matrix4
) => {
  const vbox: VisibilityBoxExtended = useMemo(() => {
    const matrix0 = matrixFromVisibilityBox(visibilityBox);
    const matrixInverse0 = matrix0.clone().invert();

    const checkIsInside = (point: Number3) => {
      const point0 = new THREE.Vector3(...point);
      return isPointInsideCuboidMatrix(point0, matrixInverse0);
    };
    return { ...visibilityBox, matrix: matrix0, matrixInverse: matrixInverse0, checkIsInside };
  }, [visibilityBox]);

  useEffect(() => {
    matrix.copy(vbox.matrix);
    matrixInverse.copy(vbox.matrixInverse);
  }, [vbox, matrix, matrixInverse]);

  return vbox;
};

type Props = {
  visibilityBoxOnParent?: RotatedVisibilityBoxProps;
  setVisibilityBoxOnParent?: (newBox: RotatedVisibilityBoxProps) => void;
  smallVisibilityBoxOnParent?: RotatedVisibilityBoxProps;
  setSmallVisibilityBoxOnParent?: (newBox: RotatedVisibilityBoxProps) => void;
  vboxUniformsReference: React.MutableRefObject<VisibilityBoxUniforms>;
  imageAnchorsExtraRange?: Number3;
  pointCloudMaterialMap: Map<string, PointCloudMaterial>;
  materials: Set<THREE.Material> | undefined;
};

export const useVisibilityBox = ({
  visibilityBoxOnParent,
  setVisibilityBoxOnParent,
  smallVisibilityBoxOnParent,
  setSmallVisibilityBoxOnParent,
  vboxUniformsReference,
  imageAnchorsExtraRange,
  pointCloudMaterialMap,
  materials,
}: Props) => {
  const { clippingPlanes } = useAbyssViewerContext();
  const [visibilityBoxLocal, setVisibilityBoxLocal] =
    useState<RotatedVisibilityBoxProps>(defaultVisibilityBox);
  const [smallVisibilityBoxLocal, setSmallVisibilityBoxLocal] =
    useState<RotatedVisibilityBoxProps>(defaultVisibilityBox);

  const visibilityBox = visibilityBoxOnParent ?? visibilityBoxLocal;
  const setVisibilityBox = setVisibilityBoxOnParent ?? setVisibilityBoxLocal;

  const smallVisibilityBox = smallVisibilityBoxOnParent ?? smallVisibilityBoxLocal;
  const setSmallVisibilityBox = setSmallVisibilityBoxOnParent ?? setSmallVisibilityBoxLocal;

  const imageAnchorsVisibilityBox: RotatedVisibilityBoxProps = useMemo(() => {
    const { scale, ...rest } = visibilityBox;
    const [x, y, z] = imageAnchorsExtraRange ?? defaultImageAnchorsExtraRange;
    return { ...rest, scale: [scale[0] + 2 * x, scale[1] + 2 * y, scale[2] + 2 * z] };
  }, [visibilityBox, imageAnchorsExtraRange]);

  const smallVboxExtended = useVisibilityBoxExtended(
    smallVisibilityBox,
    vboxUniformsReference.current.smallMatrix,
    vboxUniformsReference.current.smallMatrixInverse
  );
  const vboxExtended = useVisibilityBoxExtended(
    visibilityBox,
    vboxUniformsReference.current.matrix,
    vboxUniformsReference.current.matrixInverse
  );

  const anchors = useRef({
    matrix: new THREE.Matrix4(),
    matrixInverse: new THREE.Matrix4(),
  });
  const imageAnchorsVboxExtended = useVisibilityBoxExtended(
    imageAnchorsVisibilityBox,
    anchors.current.matrix,
    anchors.current.matrixInverse
  );

  // clipping planes for 1x1x1 cube centered at the origin
  const initialClippingPlanes = useMemo(() => {
    return [...Array(6).keys()].map((_, index) => {
      const normal = [0, 0, 0];
      const sign = index & 1;
      const axis = Math.trunc(index / 2);
      normal[axis] = sign === 0 ? -1 : 1;
      return new THREE.Plane(new THREE.Vector3(...normal), 0.5);
    });
  }, []);

  const onVisibilityBoxUniformsUpdate = useCallback(
    (vboxUniforms: VisibilityBoxUniforms) => {
      pointCloudMaterialMap.forEach((material) => {
        material.visibilityBoxNeedsUpdate(vboxUniforms);
      });
      if (clippingPlanes) {
        initialClippingPlanes.forEach((initialPlane, index) => {
          const plane = clippingPlanes[index];
          plane.copy(initialPlane);
          plane.applyMatrix4(vboxUniforms.matrix);
        });
        materials?.forEach((material) => {
          material.needsUpdate = true;
        });
      }
    },
    [pointCloudMaterialMap, materials, initialClippingPlanes, clippingPlanes]
  );

  const grayArea = visibilityBox.style?.grayArea ?? defaultVisibilityBoxStyle.grayArea;

  useEffect(() => {
    const vboxUniforms = vboxUniformsReference.current;
    vboxUniforms.colorMultiplyAndAlpha.set(grayArea.multiply, grayArea.alpha);
    vboxUniforms.additiveColor.setFromColor(new THREE.Color(grayArea.additiveColor));
    onVisibilityBoxUniformsUpdate(vboxUniforms);
  }, [vboxUniformsReference, grayArea, onVisibilityBoxUniformsUpdate]);

  useEffect(() => {
    onVisibilityBoxUniformsUpdate(vboxUniformsReference.current);
  }, [vboxUniformsReference, vboxExtended, smallVboxExtended, onVisibilityBoxUniformsUpdate]);

  return {
    visibilityBox: vboxExtended,
    setVisibilityBox,
    smallVisibilityBox: smallVboxExtended,
    setSmallVisibilityBox,
    imageAnchorsVisibilityBox: imageAnchorsVboxExtended,
  };
};
